import { FiTarget } from 'react-icons/fi';

export const cleftSentencesData = {
  courseId: 'cleft-sentences',
  courseType: 'grammar',
  title: 'Cleft Sentences',
  icon: FiTarget,
  overview: {
    impact: 'Focus the key information so examiners hear the point you actually want to highlight',
    focus: 'It-clefts (It is X that…) and wh-clefts (What I need is…)',
    uses: 'Band 7–8 Writing Task 2 emphasis and Speaking Parts 2–3 when you correct, contrast, or highlight a reason',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'Why clefts raise your band',
      content: `<p>A <strong>cleft sentence</strong> splits one idea into two clauses so the listener or reader cannot miss the focused piece. Instead of “The methodology weakened the study,” you write “<em>It was the methodology that</em> weakened the study.” The grammar is not decoration: it tells the examiner <em>which</em> information is new, contrastive, or corrective.</p>
<p>IELTS rewards this under Grammatical Range and Accuracy and, in speaking, under Fluency and Coherence. Candidates who only produce subject–verb–object sentences stay around Band 6 even when their ideas are sound. Used once or twice per answer, a cleft sounds controlled; used in every sentence, it sounds rehearsed.</p>
<p>Two patterns cover almost every exam use: the <strong>it-cleft</strong> (<em>It is/was X that/who…</em>) and the <strong>wh-cleft</strong> or pseudo-cleft (<em>What + clause + is…</em>). Both let you highlight a cause, a person, a time, or a recommendation without adding extra ideas.</p>`,
      points: [
        'Clefts highlight one element: the cause, the person, the time, or the recommendation.',
        'They are strongest when you contrast (“not the funding, the timeline”) or correct a previous idea.',
        'One well-placed cleft per paragraph or spoken answer is enough for Band 7+.',
        'Keep the focused element short; a long noun phrase after “It was…” becomes clumsy.',
      ],
      examples: [
        { label: 'Plain', text: 'The funding delayed the project.' },
        { label: 'It-cleft', text: 'It was the funding that delayed the project.' },
        { label: 'Wh-cleft', text: 'What delayed the project was the funding.' },
      ],
    },
    {
      title: 'It-clefts: It is / It was X that…',
      content: `<p>The it-cleft formula is <strong>It + is/was + focused information + that/who + the rest of the clause</strong>. Use <em>is</em> for present or general claims and <em>was</em> for finished past events. Use <em>who</em> for people and <em>that</em> for things, times, places, and reasons. In formal writing, <em>that</em> is safer than omitting the relative word.</p>
<p>Typical IELTS moves: highlight a cause (“It is poor planning that undermines most projects”), a person (“It was local councils that funded the scheme”), a time (“It was only after the reform that results improved”), or a contrast (“It is not cost but safety that should decide the policy”).</p>
<p>Tense must match the situation. “It is the methodology that weakened the study” mixes present focus with a past verb and sounds careless. Pair present focus with present verbs, or past focus with past verbs: “It <em>was</em> the methodology that weakened the study.”</p>`,
      points: [
        'It is / It was + noun or noun phrase + that / who + clause.',
        'Use “who” for people; use “that” for things, times, and reasons.',
        'Match is/was to the time you are talking about.',
        'Negative contrast is natural: “It is not X but Y that…”',
      ],
      table: {
        headers: ['Focus', 'Cleft', 'Why it works'],
        rows: [
          ['Cause', 'It was the methodology that weakened the study.', 'Puts the fault on method, not the whole paper.'],
          ['Person', 'It is teachers who notice the first signs of stress.', 'Names the agent clearly.'],
          ['Time', 'It was only last year that the law changed.', 'Highlights when, not what.'],
          ['Contrast', 'It is not technology but training that raises scores.', 'Corrects a common claim.'],
        ],
      },
      examples: [
        { label: 'Weak', text: 'The government should fund libraries, I think, more than stadiums.' },
        { label: 'Strong', text: 'It is public libraries, rather than stadiums, that deserve the next round of funding.' },
      ],
    },
    {
      title: 'Wh-clefts: What… is…',
      content: `<p>A <strong>wh-cleft</strong> (also called a pseudo-cleft) starts with a clause headed by <em>what</em>, <em>where</em>, or sometimes <em>why</em>, then finishes with <em>is/was</em> plus the focused information. “What the data shows is a clear decline.” The first clause names the topic; the second clause delivers the news.</p>
<p>This pattern is especially useful in Speaking Part 3 when you need a second to organise the point: “What I would argue is that cities need cheaper housing, not more roads.” It also works in Task 2 topic sentences: “What this essay will argue is not that tourism should stop, but that it should be taxed more fairly.” Avoid starting every paragraph this way; examiners notice the template.</p>
<p>Keep the <em>what</em>-clause grammatical. “What I need is more time” is complete. “What I need more time” is not a cleft — it is a fragment. The verb <em>is/was</em> after the <em>what</em>-clause is not optional.</p>`,
      points: [
        'Formula: What + subject + verb + is/was + focused noun or clause.',
        'Useful spoken frames: “What I mean is…”, “What concerns me is…”, “What the evidence suggests is…”',
        'Reverse the pattern for variety: “A clear decline is what the data shows.” Use this sparingly.',
        'Do not drop “is/was” after the what-clause.',
      ],
      examples: [
        { label: 'Speaking', text: 'What I need is a concrete example, not another statistic.' },
        { label: 'Writing', text: 'What the data shows is a clear decline in rural employment after 2010.' },
        { label: 'Correction', text: 'What I meant was the local government, not the national one.' },
      ],
    },
    {
      title: 'Using clefts under exam pressure',
      content: `<p>In Writing Task 2, place an it-cleft in a topic sentence or a rebuttal: “It is short-term cost, not long-term value, that opponents emphasise.” In Task 1, a single cleft can spotlight the main feature: “It is the 2019 figure that stands out.” Do not cleft every comparison; overviews should stay simple.</p>
<p>In speaking, a cleft is a repair and emphasis tool. If you start vaguely, finish with a cleft: “The main issue is… what really matters is access to childcare.” If the examiner rephrases your point, you can confirm with “Yes — it is the cost that worries people, not the idea itself.”</p>
<p>Common errors: mixing tenses, using “what” without “is,” clefting pronouns that add no focus (“It is it that…”), and stacking two clefts in one sentence. If the sentence would be clearer without the cleft, drop it.</p>`,
      points: [
        'One cleft per body paragraph or spoken turn is a safe maximum.',
        'Use clefts to correct, contrast, or name a cause — not to sound fancy.',
        'If you hesitate, finish a simple sentence first, then add a cleft as a follow-up.',
      ],
      warning: 'A forced cleft with a grammar slip scores lower than a plain accurate sentence.',
    },
  ],
  exercises: [
    {
      title: 'Complete the it-cleft',
      type: 'select',
      instructions: 'Choose the form that creates a correct, natural it-cleft.',
      questions: [
        {
          text: '____ the funding that delayed the project.',
          options: ['It was', 'There was', 'What was'],
          answer: 'It was',
          explanation: 'An it-cleft needs “It is/was + focused noun + that…”. “There was” only exists; “What was” starts a question or a broken wh-cleft.',
        },
        {
          text: 'It is local teachers ____ notice the first signs of burnout.',
          options: ['who', 'which', 'what'],
          answer: 'who',
          explanation: 'When the focused element is a person or group of people, “who” is the natural relative word. “Which” is for things; “what” does not follow an it-cleft noun.',
        },
        {
          text: 'Which sentence correctly highlights a past cause?',
          options: [
            'It is the methodology that weakened the study.',
            'It was the methodology that weakened the study.',
            'It were the methodology that weaken the study.',
          ],
          answer: 'It was the methodology that weakened the study.',
          explanation: 'The weakening already happened, so both the cleft verb and the clause verb stay in the past. “It is… weakened” mixes tenses; “It were” is ungrammatical with a singular noun.',
        },
        {
          text: 'Which it-cleft best contrasts two ideas?',
          options: [
            'It is not the salary but the hours that staff complain about.',
            'It is the salary and the hours that maybe staff.',
            'There is not the salary that staff complain.',
          ],
          answer: 'It is not the salary but the hours that staff complain about.',
          explanation: '“It is not X but Y that…” is the standard contrast cleft. The other options are incomplete or mix “there is” with a cleft pattern.',
        },
      ],
    },
    {
      title: 'Build the wh-cleft',
      type: 'fill-in',
      instructions: 'Type the missing word that completes a grammatical wh-cleft.',
      questions: [
        {
          text: 'What the data shows ___ a clear decline.',
          answer: 'is',
          explanation: 'A wh-cleft needs the linking verb after the what-clause: What + clause + is/was + focus. Without “is,” the sentence is a fragment.',
        },
        {
          text: '___ I need is a clearer example, not another definition.',
          answer: 'What',
          explanation: 'Wh-clefts typically open with “What” plus a clause. “What I need is…” is the most common spoken frame for naming a requirement.',
        },
        {
          text: 'What concerns residents ___ the lack of late-night buses.',
          answer: 'is',
          explanation: 'The focused information (“the lack of late-night buses”) is a singular noun phrase, so the linking verb is “is,” not “are.”',
        },
        {
          text: 'What I meant ___ the local government, not the national one.',
          answer: 'was',
          explanation: 'You are correcting something already said, so the cleft verb is past: “What I meant was…” This is a natural spoken repair.',
        },
      ],
    },
    {
      title: 'Choose the higher-scoring sentence',
      type: 'select',
      instructions: 'Pick the sentence that uses a cleft accurately to focus the key information.',
      questions: [
        {
          text: 'Which version would score higher in a Task 2 topic sentence?',
          options: [
            'It was short-term thinking that caused the policy to fail.',
            'It short-term thinking caused the policy to fail.',
            'What short-term thinking the policy failed.',
          ],
          answer: 'It was short-term thinking that caused the policy to fail.',
          explanation: 'The first option is a complete it-cleft with matching past tense. The others drop “was/that” or scramble the wh-cleft formula.',
        },
        {
          text: 'Which spoken follow-up sounds most natural after a vague start?',
          options: [
            'What I mean is that parents need cheaper childcare, not more slogans.',
            'It is what I mean parents need cheaper childcare.',
            'What is I mean cheaper childcare.',
          ],
          answer: 'What I mean is that parents need cheaper childcare, not more slogans.',
          explanation: '“What I mean is…” is a standard clarification cleft. Mixing “It is what I mean” plus a clause without “that/is” sounds like two patterns colliding.',
        },
        {
          text: 'When should you avoid a cleft?',
          options: [
            'When a simple sentence already makes the point clearly',
            'Whenever you want a Band 8 grammar feature',
            'In the first sentence of every paragraph',
          ],
          answer: 'When a simple sentence already makes the point clearly',
          explanation: 'Clefts exist to highlight. If nothing needs highlighting, a plain sentence is clearer and less likely to produce an error.',
        },
        {
          text: 'Which relative word completes this correctly? “It was in 2018 ____ the factory closed.”',
          options: ['that', 'who', 'what'],
          answer: 'that',
          explanation: 'The focused element is a time phrase (“in 2018”), so “that” links the cleft. “Who” is for people; “what” does not follow this it-cleft slot.',
        },
      ],
    },
  ],
  tips: [
    'Use a cleft to correct or highlight, not in every sentence — one per answer is usually enough.',
    'Lock the formula before you invent content: It was X that… / What Y is…',
    'Match is/was to the time of the event you are focusing.',
    'In speaking, a cleft is a clean way to finish a hesitant start: “What I meant was…”',
  ],
  dailyPractice: [
    'Take one news headline and rewrite it as an it-cleft and a wh-cleft.',
    'In a 40-second Part 3 answer, use exactly one cleft to name the main cause.',
    'Edit a Task 2 body paragraph: add one contrast cleft (“It is not X but Y that…”).',
    'Record yourself repairing a slip with “What I meant was…” instead of stopping.',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Cleft Sentences grammar course for IELTS. Teaches it-clefts (It is/was X that/who…) and wh-clefts (What… is…) for emphasis, contrast, and self-correction in Writing Task 2 and Speaking Parts 2–3. Stresses tense agreement, one-cleft-per-turn control, and avoiding forced or incomplete clefts.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...cleftSentencesData,
      title: cleftSentencesData.title + ' (Foundations)',
      overview: { ...cleftSentencesData.overview, duration: '15-20 minutes' },
      sections: cleftSentencesData.sections.slice(0, Math.max(2, Math.ceil(cleftSentencesData.sections.length / 2))),
      exercises: cleftSentencesData.exercises.slice(0, 1).map((ex) => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...cleftSentencesData,
      title: cleftSentencesData.title + ' (Advanced)',
      overview: { ...cleftSentencesData.overview, duration: '30-35 minutes' },
      sections: cleftSentencesData.sections,
      exercises: cleftSentencesData.exercises.map((ex) => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(cleftSentencesData.tips || []),
        'For Band 8+: use a cleft only when the focused element is new or contrastive.',
        'Advanced tip: combine a concession with a cleft — “While cost matters, it is safety that should decide the policy.”',
      ],
    };
  }
  return cleftSentencesData;
}
